import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Input, InputNumber, Modal, Pagination, Spin, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  createCollaboration,
  getCommerceProducts,
  getInfluencerCollectionPage,
  getInfluencerPage
} from '../../../store/request';
import {
  SHOP_PRODUCT_PAGE_SIZE,
  commissionRateToPercent,
  percentToCommissionRate,
  type ShopProduct
} from '../../../types/commerce';
import type { InfluencerCollectionRecord, InfluencerRecord } from '../../../types/influencer';

type TalentTab = 'all' | 'mine' | 'square' | 'paste';

type TalentSource = {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  email?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
  isBlacklist?: boolean | null;
  status?: number | null;
};

type PickerTalent = {
  id: string;
  name: string;
  handle: string;
  avatarUrl: string;
  initial: string;
  color: string;
  meta: string;
};

type CreateTargetInviteModalProps = {
  open: boolean;
  /** 达人广场已勾选的达人。打开时默认选中，并显示在「全部」里。 */
  presetTalents?: TalentSource[];
  onClose: () => void;
  onSuccess?: () => void;
};

function productKey(item: ShopProduct, index: number, page: number) {
  return item.productId?.trim() || `product-${page}-${index}`;
}

function productTitleOf(item: ShopProduct) {
  return item.productName?.trim() || item.productId?.trim() || '—';
}

function toPickerTalent(row: TalentSource): PickerTalent | null {
  const influencerId = String(row.influencerId ?? '').trim();
  const creatorOpenId = String(row.creatorOpenId ?? '').trim();
  const id = influencerId || creatorOpenId;
  if (!id) return null;
  const name = row.nickname || row.username || id;
  const handle = row.username?.replace(/^@/, '').trim() || '';
  const categories = row.categoryNames?.filter(Boolean).slice(0, 2).join(' · ') ?? '';
  return {
    id,
    name,
    handle,
    avatarUrl: row.avatarUrl?.trim() || '',
    initial: name.slice(0, 1).toUpperCase() || '?',
    color: '#596ae8',
    meta: [handle ? `@${handle}` : '', row.selectionRegion, categories].filter(Boolean).join(' · ') || '—'
  };
}

export function CreateTargetInviteModal({ open, presetTalents, onClose, onSuccess }: CreateTargetInviteModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { shopId, shops } = useSelectedShop();
  const [name, setName] = useState('');
  const [deadline, setDeadline] = useState<Dayjs | null>(null);
  const [contactEmail, setContactEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productReloadKey, setProductReloadKey] = useState(0);
  const [productKeywordInput, setProductKeywordInput] = useState('');
  const [productKeyword, setProductKeyword] = useState('');
  const appliedProductKeywordRef = useRef('');
  const refreshProductsRequested = useRef(false);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [tab, setTab] = useState<TalentTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickedTalents, setPickedTalents] = useState<PickerTalent[]>([]);
  const [mineRecords, setMineRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineLoading, setMineLoading] = useState(false);
  const [squareRecords, setSquareRecords] = useState<InfluencerRecord[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);

  const shopName = shops.find((item) => item.shopId === shopId)?.shopName?.trim() || '—';
  const currentShopId = shopId?.trim() || '';

  useEffect(() => {
    if (!open) return;
    setName('');
    setDeadline(null);
    setContactEmail('');
    setInviteMessage('');
    setSubmitting(false);
    setProducts([]);
    setProductPage(1);
    setProductTotal(0);
    setProductIds([]);
    setRates({});
    setProductReloadKey(0);
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
    setKeyword('');
    setPasteText('');
    const fromRows = (presetTalents ?? []).map(toPickerTalent).filter((item): item is PickerTalent => item != null);
    setSelectedIds(Array.from(new Set(fromRows.map((item) => item.id))));
    setPickedTalents(fromRows);
    setTab(fromRows.length > 0 ? 'all' : 'mine');
    setSquareRecords([]);
  }, [open, presetTalents]);

  useEffect(() => {
    setProductPage(1);
    setProductIds([]);
    setRates({});
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
  }, [currentShopId]);

  useEffect(() => {
    if (!open) return;
    if (!currentShopId) {
      setProducts([]);
      setProductTotal(0);
      setProductsLoading(false);
      return;
    }
    let cancelled = false;
    setProductsLoading(true);
    const notify = refreshProductsRequested.current;
    refreshProductsRequested.current = false;
    const keyword = productKeyword.trim();
    getCommerceProducts({
      shopId: currentShopId,
      pageNum: productPage,
      pageSize: SHOP_PRODUCT_PAGE_SIZE,
      ...(keyword ? { keyword } : {})
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
          return;
        }
        const records = res.content?.records;
        const total = res.content?.total;
        const list = Array.isArray(records) ? records : [];
        setProducts(list);
        setProductTotal(typeof total === 'number' ? total : list.length);
        setRates((prev) => {
          const next = { ...prev };
          list.forEach((item, index) => {
            const key = productKey(item, index, productPage);
            if (next[key] != null) return;
            const percent = commissionRateToPercent(item.commissionRate);
            if (percent != null) next[key] = percent;
          });
          return next;
        });
        if (notify) message.success(res.message || t('invitation.product_refreshed'));
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, productPage, productKeyword, productReloadKey, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = productKeywordInput.trim();
      if (appliedProductKeywordRef.current === next) return;
      appliedProductKeywordRef.current = next;
      setProductKeyword(next);
      setProductPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [productKeywordInput]);

  const mineKeyword = tab === 'mine' ? keyword.trim() : '';
  const squareKeyword = tab === 'square' ? keyword.trim() : '';

  useEffect(() => {
    if (!open) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setMineRecords([]);
      setMineTotal(0);
      setMineLoading(false);
      return;
    }
    let cancelled = false;
    setMineLoading(true);
    const timer = window.setTimeout(() => {
      const loadAll = async () => {
        const pageSize = 50;
        const all: InfluencerCollectionRecord[] = [];
        let pageNum = 1;
        let total = 0;
        while (pageNum <= 40) {
          const res = await getInfluencerCollectionPage({
            userCode,
            pageNum,
            pageSize,
            ...(mineKeyword ? { keyword: mineKeyword } : {})
          });
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
            return;
          }
          const page = res.content;
          const records = page?.records;
          const list = Array.isArray(records) ? records : [];
          total = typeof page?.total === 'number' ? page.total : all.length + list.length;
          all.push(...list);
          if (list.length === 0 || all.length >= total) break;
          pageNum += 1;
        }
        if (cancelled) return;
        setMineRecords(all);
        setMineTotal(total);
      };
      loadAll()
        .catch(() => {
          if (!cancelled) {
            message.error(t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setMineLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, account?.userCode, mineKeyword, t]);

  useEffect(() => {
    if (!open || tab !== 'square') return;
    let cancelled = false;
    setSquareLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerPage({
        pageNum: 1,
        pageSize: 50,
        ...(squareKeyword ? { keyword: squareKeyword } : {})
      })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('talent.load_failed'));
            setSquareRecords([]);
            return;
          }
          const list = res.content?.records;
          setSquareRecords(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('talent.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setSquareLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, tab, squareKeyword, t]);

  const mineTalents = useMemo(
    () => mineRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [mineRecords]
  );

  const squareTalents = useMemo(
    () => squareRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [squareRecords]
  );

  const talentById = useMemo(() => {
    const map = new Map<string, PickerTalent>();
    pickedTalents.forEach((item) => map.set(item.id, item));
    mineTalents.forEach((item) => map.set(item.id, item));
    squareTalents.forEach((item) => map.set(item.id, item));
    return map;
  }, [pickedTalents, mineTalents, squareTalents]);

  const listSource = useMemo(() => {
    if (tab === 'mine') return mineTalents;
    if (tab === 'square') return squareTalents;
    if (tab === 'all') {
      return selectedIds
        .map((id) => talentById.get(id))
        .filter((item): item is PickerTalent => item != null);
    }
    return [];
  }, [tab, selectedIds, mineTalents, squareTalents, talentById]);

  const toggleTalent = (talent: PickerTalent, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? (prev.includes(talent.id) ? prev : [...prev, talent.id]) : prev.filter((id) => id !== talent.id)
    );
    setPickedTalents((prev) =>
      checked
        ? prev.some((item) => item.id === talent.id)
          ? prev
          : [...prev, talent]
        : prev.filter((item) => item.id !== talent.id)
    );
  };

  const toggleProduct = (id: string, checked: boolean) => {
    setProductIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)));
  };

  const setRate = (id: string, value: number | null) => {
    if (value === null) return;
    setRates((prev) => ({ ...prev, [id]: value }));
  };

  const refreshProducts = () => {
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    refreshProductsRequested.current = true;
    setProductReloadKey((key) => key + 1);
  };

  const submit = async () => {
    if (submitting) return;
    const currentName = name.trim();
    const email = contactEmail.trim();
    const note = inviteMessage.trim();
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    if (!currentName) {
      message.warning(t('invitation.create_name_required'));
      return;
    }
    if (!deadline) {
      message.warning(t('invitation.create_deadline_required'));
      return;
    }
    if (!email) {
      message.warning(t('invitation.contact_email_required'));
      return;
    }
    if (productIds.length === 0) {
      message.warning(t('invitation.pick_product'));
      return;
    }
    if (selectedIds.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    const selectedProducts = productIds.map((id) => ({
      productId: id.startsWith('product-') ? '' : id,
      ratePercent: rates[id]
    }));
    if (
      selectedProducts.some(
        (item) => !item.productId || item.productId.length > 64
      )
    ) {
      message.warning(t('invitation.product_id_invalid'));
      return;
    }
    if (
      selectedProducts.some(
        (item) =>
          typeof item.ratePercent !== 'number' ||
          !Number.isFinite(item.ratePercent) ||
          item.ratePercent < 0
      )
    ) {
      message.warning(t('invitation.commission_required'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await createCollaboration({
        shopId: currentShopId,
        name: currentName,
        endTime: deadline.endOf('day').toISOString(),
        contactEmail: email,
        ...(note ? { message: note } : {}),
        products: selectedProducts.map((item) => ({
          productId: item.productId,
          targetCommissionRate: percentToCommissionRate(item.ratePercent as number)
        })),
        influencerIds: selectedIds
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.create_failed'));
        return;
      }
      message.success(res.message || res.content?.message || t('invitation.create_success'));
      const skipped = res.content?.skippedInfluencerIds?.map((id) => id.trim()).filter(Boolean) ?? [];
      if (skipped.length > 0) {
        message.warning(t('invitation.create_skipped', { count: skipped.length }));
      }
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('invitation.create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const tabBtnClass = (active: boolean) =>
    active
      ? 'rounded-full border-0 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700'
      : 'cursor-pointer rounded-full border border-[#eef0f4] bg-white px-3 py-1 text-xs text-slate-600 hover:border-emerald-200';

  return (
    <Modal
      title={t('invitation.create_target')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={680}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingTop: 12 } }}
      closeIcon={
        <span className='cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700'>{t('common.close')}</span>
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={submitting} onClick={submit}>
            {t('invitation.submit_invite')}
          </Button>
        </div>
      }>
      <div className='space-y-3'>
        <div>
          <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.create_name')}</div>
          <Input
            allowClear
            maxLength={255}
            placeholder={t('invitation.create_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.create_deadline')}</div>
          <DatePicker className='w-full' format='YYYY/MM/DD' value={deadline} onChange={setDeadline} />
        </div>
        <div>
          <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.contact_email')}</div>
          <Input
            allowClear
            type='email'
            placeholder={t('invitation.contact_email_placeholder')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </div>
        <div>
          <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.invite_message')}</div>
          <Input.TextArea
            rows={2}
            placeholder={t('invitation.invite_message_placeholder')}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
          />
        </div>
        <div>
          <div className='mb-1.5 text-sm font-medium text-slate-700'>{t('invitation.mounted_products')}</div>
          <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
            <span className='text-xs text-slate-500'>
              {t('invitation.current_shop')} <span className='font-medium text-slate-700'>{shopName}</span>
              {' · '}
              {t('invitation.rate_adjustable')}
            </span>
            <Button
              size='small'
              className='rounded-lg'
              icon={<ReloadOutlined />}
              loading={productsLoading}
              onClick={refreshProducts}>
              {t('invitation.refresh')}
            </Button>
          </div>
          <Input
            allowClear
            className='mb-2 rounded-lg'
            placeholder={t('invitation.product_keyword')}
            prefix={<SearchOutlined className='text-slate-400' />}
            value={productKeywordInput}
            onChange={(event) => setProductKeywordInput(event.target.value)}
          />
          <div className='overflow-hidden rounded-lg border border-[#eef0f4] bg-white'>
            {productsLoading ? (
              <div className='flex justify-center py-6'>
                <Spin size='small' />
              </div>
            ) : null}
            {!productsLoading && products.length === 0 ? (
              <p className='mb-0 px-3 py-6 text-center text-sm text-slate-400'>
                {!currentShopId ? t('invitation.invite_shop_required') : t('invitation.product_empty')}
              </p>
            ) : null}
            {!productsLoading
              ? products.map((item, index) => {
                const id = productKey(item, index, productPage);
                return (
                  <div
                    key={id}
                    className='flex flex-wrap items-center gap-3 border-b border-[#eef0f4] px-3 py-3 last:border-b-0 sm:flex-nowrap'>
                    <Checkbox checked={productIds.includes(id)} onChange={(e) => toggleProduct(id, e.target.checked)} />
                    {item.mainImageUrl?.trim() ? (
                      <img
                        src={item.mainImageUrl}
                        alt=''
                        className='size-[56px] shrink-0 rounded-lg object-cover'
                      />
                    ) : (
                      <span className='inline-flex size-[56px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-semibold tracking-wide text-slate-400'>
                        SKU
                      </span>
                    )}
                    <div className='min-w-0 w-[340px] shrink break-words text-[13px] font-semibold leading-snug text-slate-800'>
                      {productTitleOf(item)}
                    </div>
                    <div className='ml-auto flex shrink-0 items-center gap-2 pl-2 sm:pl-0'>
                      <span className='text-xs text-slate-500'>{t('invitation.target_commission')}</span>
                      <InputNumber
                        min={0}
                        precision={0}
                        size='small'
                        className='w-[72px]'
                        value={rates[id]}
                        onChange={(value) => setRate(id, value)}
                      />
                      <span className='text-xs text-slate-400'>%</span>
                    </div>
                  </div>
                );
              })
              : null}
            {!productsLoading && productTotal > SHOP_PRODUCT_PAGE_SIZE ? (
              <div className='flex justify-end border-t border-[#eef0f4] px-3 py-2'>
                <Pagination
                  size='small'
                  current={productPage}
                  pageSize={SHOP_PRODUCT_PAGE_SIZE}
                  total={productTotal}
                  showSizeChanger={false}
                  onChange={(page) => setProductPage(page)}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div>
          <div className='mb-1 text-sm font-medium text-slate-700'>{t('invitation.add_talent_section')}</div>
          <p className='mb-2 mt-0 text-xs leading-relaxed text-slate-400'>
            {selectedIds.length === 0
              ? t('invitation.none_selected')
              : t('invitation.selected_count', { count: selectedIds.length })}
          </p>
          <Input
            allowClear
            placeholder={t('invitation.search_placeholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div className='mt-3 flex flex-wrap gap-2'>
            <button type='button' className={tabBtnClass(tab === 'all')} onClick={() => setTab('all')}>
              {t('invitation.all_count', { count: selectedIds.length })}
            </button>
            <button type='button' className={tabBtnClass(tab === 'mine')} onClick={() => setTab('mine')}>
              {t('invitation.my_talents', { count: mineTalents.length })}
            </button>
            <button type='button' className={tabBtnClass(tab === 'square')} onClick={() => setTab('square')}>
              {t('invitation.search_square')}
            </button>
            {/* <button type='button' className={tabBtnClass(tab === 'paste')} onClick={() => setTab('paste')}>
              {t('invitation.paste_list')}
            </button>
            <button type='button' className={tabBtnClass(false)} onClick={selectAllVisible}>
              {t('invitation.select_all')}
            </button> */}
          </div>
          {tab === 'paste' ? (
            <Input.TextArea
              className='mt-3'
              rows={3}
              placeholder={t('invitation.paste_placeholder')}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
          ) : (
            <div className='mt-3 max-h-[220px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1'>
              {(tab === 'mine' && mineLoading) || (tab === 'square' && squareLoading) ? (
                <div className='flex justify-center py-6'>
                  <Spin size='small' />
                </div>
              ) : null}
              {listSource.map((talent) => (
                <label key={talent.id} className='flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white'>
                  <Checkbox checked={selectedIds.includes(talent.id)} onChange={(e) => toggleTalent(talent, e.target.checked)} />
                  {talent.avatarUrl ? (
                    <img src={talent.avatarUrl} alt='' className='size-8 shrink-0 rounded-full object-cover' />
                  ) : (
                    <span
                      className='inline-flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white'
                      style={{ backgroundColor: talent.color }}>
                      {talent.initial}
                    </span>
                  )}
                  <span className='min-w-0 flex-1'>
                    <span className='block text-sm font-semibold text-slate-800'>{talent.name}</span>
                    <span className='block truncate text-xs text-slate-500'>{talent.meta}</span>
                  </span>
                </label>
              ))}
              {((tab !== 'mine' && tab !== 'square') ||
                (tab === 'mine' && !mineLoading) ||
                (tab === 'square' && !squareLoading)) &&
              listSource.length === 0 ? (
                <p className='px-2 py-6 text-center text-sm text-slate-400'>
                  {tab === 'all' ? t('invitation.empty_selected') : t('invitation.no_match')}
                </p>
              ) : null}
            </div>
          )}
          {/* <p className='mb-0 mt-2 text-xs leading-relaxed text-slate-400'>{t('invitation.talent_default_hint')}</p> */}
        </div>
        {/* <p className='mb-0 text-xs leading-relaxed text-slate-400'>{t('invitation.submit_invite_note')}</p> */}
      </div>
    </Modal>
  );
}
